import type { RequestFn } from '../http';

/** Ergebnis eines dokumentierten Anrufs. */
export type CallOutcome =
  | 'REACHED'
  | 'NO_ANSWER'
  | 'VOICEMAIL'
  | 'BUSY'
  | 'WRONG_NUMBER'
  | 'CALLBACK_AGREED';

/** Woraus heraus telefoniert wurde — für den Rücksprung in die Oberfläche. */
export type CallOriginType =
  | 'ticket'
  | 'chat'
  | 'callback'
  | 'customer'
  | 'contact'
  | 'unknown_caller'
  | 'directory';

export interface LogCallInput {
  phone: string;
  contactName?: string;
  customerId?: string;
  /** Kundennummer des führenden Systems, wenn die Portal-Id nicht vorliegt. */
  weclappCustomerId?: string;
  cwCompanyId?: number;
  contactId?: string;
  ticketProvider?: 'weclapp' | 'connectwise';
  ticketId?: string;
  ticketNumber?: string;
  originType?: CallOriginType;
  originId?: string;
  outcome: CallOutcome;
  note?: string;
  /** ISO-Zeitpunkt der Wiedervorlage. */
  followUpAt?: string;
  /** Beginn des Anrufs (Klick auf „Anrufen"). */
  startedAt?: string;
  durationSec?: number;
  direction?: 'OUTBOUND' | 'INBOUND';
}

export interface LogCallResult {
  id: string;
  customerId: string | null;
  contactId: string | null;
  /** Gesetzt, wenn der interne Vermerk am Ticket NICHT geschrieben werden konnte. */
  ticketCommentError: string | null;
  /** Aus der Wiedervorlage ist eine Erinnerung geworden (Aufgaben & Erinnerungen). */
  followUpTodoId?: string | null;
}

export interface CallHistoryItem {
  id: string;
  /** `portal` = im Portal dokumentiert, `pbx` = aus der Telefonanlage. */
  source: 'portal' | 'pbx';
  direction: string;
  phoneE164: string;
  contactName: string | null;
  startedAt: string;
  durationSec: number | null;
  outcome: CallOutcome | null;
  /** Nur aus der Telefonanlage bekannt. */
  answered: boolean | null;
  note: string | null;
  followUpAt: string | null;
  technicianName: string | null;
  ticketNumber: string | null;
  ticketProvider: string | null;
  ticketId: string | null;
  originType: string | null;
  originId: string | null;
  ticketCommentError: string | null;
}

export interface CallHistoryParams {
  customerId?: string;
  /** Kundennummer des führenden Systems — der Kunden-Hub kennt nur die. */
  weclappCustomerId?: string;
  cwCompanyId?: number;
  phone?: string;
  ticketProvider?: string;
  ticketId?: string;
  limit?: number;
}

/** Eine anrufbare Nummer mit ihrer Bezeichnung („Zentrale", „Durchwahl", „Mobil"). */
export interface CallNumber {
  label: string;
  number: string;
}

/** Ein Anrufziel: die Firma selbst, ein Ansprechpartner oder ein Mitarbeiter. */
export interface CallTarget {
  kind: 'company' | 'contact' | 'employee';
  id: string;
  name: string;
  role: string | null;
  numbers: CallNumber[];
}

export function createCallsApi(request: RequestFn) {
  return {
    async logCall(input: LogCallInput) {
      return request<LogCallResult>('/calls', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },

    async getCallTargets(params: CallHistoryParams) {
      const qs = new URLSearchParams();
      if (params.customerId) qs.set('customerId', params.customerId);
      if (params.weclappCustomerId) qs.set('weclappCustomerId', params.weclappCustomerId);
      if (params.cwCompanyId != null) qs.set('cwCompanyId', String(params.cwCompanyId));
      return request<{ targets: CallTarget[] }>(`/calls/targets?${qs.toString()}`);
    },

    async getCallHistory(params: CallHistoryParams) {
      const qs = new URLSearchParams();
      if (params.customerId) qs.set('customerId', params.customerId);
      if (params.weclappCustomerId) qs.set('weclappCustomerId', params.weclappCustomerId);
      if (params.cwCompanyId != null) qs.set('cwCompanyId', String(params.cwCompanyId));
      if (params.phone) qs.set('phone', params.phone);
      if (params.ticketProvider) qs.set('ticketProvider', params.ticketProvider);
      if (params.ticketId) qs.set('ticketId', params.ticketId);
      if (params.limit) qs.set('limit', String(params.limit));
      return request<{ items: CallHistoryItem[] }>(`/calls?${qs.toString()}`);
    },
  };
}
